<template>
  <v-navigation-drawer v-model="drawer" @input="toggled" fixed temporary right :width="!$isMobile() ? '350px' : '100%'">
    <v-toolbar dark :color="blockColor()">
      <v-btn icon dark @click="toggled(false)">
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-toolbar-title>{{ $constText('ConstReservationTypes', form.type) }}</v-toolbar-title>
    </v-toolbar>
    <v-progress-linear v-if="!initialized" indeterminate color="primary" class="mx-auto"></v-progress-linear>
    <v-container v-if="initialized && form.type === $const('ConstReservationTypes', 'BLOCK')">
      <v-row class="pt-2">
        <v-col cols="6">
          <v-date
            v-model="form.start_date"
            prepend-icon="mdi-airplane-landing"
            :label="$lang('reservations.fields.start_date')"
            dense
          ></v-date>
        </v-col>
        <v-col cols="6">
          <v-date
            v-model="form.end_date"
            :label="$lang('reservations.fields.end_date')"
            prepend-icon="mdi-airplane-takeoff"
            dense
          ></v-date>
        </v-col>
      </v-row>
      <v-textarea
        v-model="form.block_reason"
        :label="$lang('reservations.fields.block_reason')"
      />

      <v-menu offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            class="ma-2"
            color="primary"
            :small="$isMobile()"
            :loading="loadingAggregated"
          >
            {{ $lang("main.crud.actions") }}
          </v-btn>
        </template>
        <v-list>
          <v-list-item
            @click="replaceReservation({
              type: $const('ConstReservationTypes', 'RESERVATION')
            })"
            :disabled="$cannot('EDIT_RESERVATIONS')"
          >
            <v-list-item-title>
              <v-icon>mdi-check-decagram</v-icon>
              {{ $lang('reservations.labels.convert_into_reservation') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            @click="replaceReservation({
              start_date: form.start_date,
              end_date: form.end_date,
              block_reason: form.block_reason
            })"
            :disabled="$cannot('EDIT_RESERVATIONS')"
          >
            <v-list-item-title>
              <v-icon>mdi-floppy</v-icon>
              {{ $lang('main.crud.update') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item
            v-if="$can('DELETE_RESERVATIONS')"
            :disabled="form.status === $const('ConstReservationStatuses', 'CANCELED')"
            @click="deleteReservation()"
            :loading="deletionRequest.loading"
          >
            <v-list-item-title>
              <v-icon color="error">mdi-trash-can</v-icon> {{ $lang('reservations.labels.cancel') }}
            </v-list-item-title>
          </v-list-item>
          <v-list-item :disabled="form.status !== $const('ConstReservationStatuses', 'CANCELED') && $can('FORCE_DELETE_RESERVATIONS')" @click="openForceDelete">
            <v-list-item-content>
              <v-list-item-title>
                <v-icon color="error">mdi-delete-alert</v-icon> {{ $lang('reservations.labels.force_delete') }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>

      <v-text-field
        v-model="createdDateTime"
        :label="$lang('main.crud.created_at')"
        readonly
      />
      <v-text-field
        v-model="updatedDateTime"
        :label="$lang('main.crud.updated_at')"
        readonly
      />
      <v-text-field
        v-model="form.creator.name"
        :label="$lang('main.crud.created_by')"
        readonly
      />
    </v-container>

    <v-container v-if="initialized && form.type !== $const('ConstReservationTypes', 'BLOCK')">
      <p class="text--secondary text-overline">
        {{ $lang('reservations.fields.res_id') }}: {{form.res_id}}
        <v-icon v-if="form.is_split" small color="red">mdi-call-split</v-icon>
      </p>
      <v-row>
        <v-col cols="12" md="12" xs="12" class="pa-0 ma-0">
          <v-col cols="12" xs="12" md="12" class="pt-2">
            <v-chip
              class="ml-0 mb-2"
              color="rgba(0,0,0,0.4)"
              pill
              style="overflow: hidden; color: #fff;"
            >
              <v-avatar v-if="$can('VIEW_RESERVATION_SOURCES')" left>
                <v-img :src="form.source.image_url"></v-img>
              </v-avatar>
              {{ form.client.name }}
              <country-flag
                v-if="form.client.country && $can('VIEW_CLIENT_DETAILS')"
                :country="form.client.country"
              ></country-flag>
            </v-chip>

            <p class="mb-0 wrap-text" v-if="form.client.email && $can('VIEW_CLIENT_DETAILS')">
              <v-icon class="mr-2" small>mdi-email</v-icon>
              <a :href="`mailto:${form.client.email}`">{{form.client.email}}</a>
            </p>
            <p v-if="form.client.phone && $can('VIEW_CLIENT_DETAILS')">
              <v-icon class="mr-2" small color="green">mdi-whatsapp</v-icon>
              <a :href="`https://wa.me/${$phoneFormat(form.client.phone)}`" target="_blank">
                {{$phoneFormat(form.client.phone)}}
              </a>
            </p>
            <v-divider></v-divider>
            <v-container class="px-0">
              <v-row class="pt-2">
                <v-col cols="1" class="mr-4">
                  <v-icon color="#ccc" size="20">mdi-calendar-range</v-icon>
                </v-col>
                <v-col cols="5">
                  <v-row>
                    <v-col class="ma-0 pa-0 flex-grow-0">
                      <v-icon class="mr-2">mdi-airplane-landing</v-icon>
                    </v-col>
                    <v-col class="ma-0 pa-0">
                      <h6 class="text-overline text-subtitle-2">{{ $lang('reservations.fields.start_date') }}</h6>
                      <p>{{ $toDateFormat(form.start_date) }}</p>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="5">
                  <v-container class="pa-0">
                    <v-row>
                      <v-col class="ma-0 pa-0 flex-grow-0">
                        <v-icon class="mr-2">mdi-airplane-takeoff</v-icon>
                      </v-col>
                      <v-col class="ma-0 pa-0">
                        <h6 class="text-overline text-subtitle-2">{{ $lang('reservations.fields.end_date') }}</h6>
                        <p>{{ $toDateFormat(form.end_date) }}</p>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="1" class="mr-4">
                  <v-icon color="#ccc" size="20">mdi-clock</v-icon>
                </v-col>
                <v-col cols="5">
                  <v-container class="pa-0">
                    <v-row>
                      <v-col class="ma-0 pa-0 flex-grow-0">
                        <v-icon class="mr-2">mdi-clock-in</v-icon>
                      </v-col>
                      <v-col class="ma-0 pa-0 flex-grow-1">
                        <h6 class="text-overline text-subtitle-2">{{ $lang('reservations.fields.arrival_time') }}</h6>
                        <p>{{form.arrival_time}}</p>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
                <v-col cols="5">
                  <v-container class="pa-0">
                    <v-row>
                      <v-col class="ma-0 pa-0 flex-grow-0">
                        <v-icon class="mr-2">mdi-weather-night</v-icon>
                      </v-col>
                      <v-col class="ma-0 pa-0 flex-grow-1">
                        <h6 class="text-overline text-subtitle-2">{{ $lang('reservations.fields.nights') }}</h6>
                        <p>{{ $dateDiffInDays(form.start_date, form.end_date) }}</p>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-col>
              </v-row>
            </v-container>
            <v-divider></v-divider>
            <v-container class="px-0">
              <v-row>
                <v-col cols="12" xs="12" md="12">
                  <v-chip label outlined>
                    <v-icon class="mr-1">mdi-human-male</v-icon>
                    {{ form.guests }} {{ $lang('reservations.fields.guests') }}
                  </v-chip>
                  <v-chip label outlined class="ml-2" v-if="form.children">
                    <v-icon class="mr-1">mdi-baby-face-outline</v-icon>
                    {{ form.children }} {{ $lang('reservations.fields.children') }}
                  </v-chip>
                </v-col>
              </v-row>
            </v-container>
            <v-divider></v-divider>
            <v-container class="mt-2 px-0">
              <v-row>
                <v-col cols="1" class="mr-4">
                  <v-icon color="#ccc" size="20">mdi-city</v-icon>
                </v-col>
                <v-col cols="5">
                  <v-row>
                    <v-col class="ma-0 pa-0 flex-grow-0">
                      <v-icon class="mr-2">mdi-office-building</v-icon>
                    </v-col>
                    <v-col class="ma-0 pa-0 flex-grow-1">
                      <h6
                        class="text-overline text-subtitle-2"
                      >{{ $lang('reservations.fields.property') }}</h6>
                      <p class="mb-0">{{form.property.name}}</p>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="5">
                  <v-row>
                    <v-col class="ma-0 pa-0 flex-grow-0">
                      <v-icon class="mr-2">mdi-bed</v-icon>
                    </v-col>
                    <v-col class="ma-0 pa-0 flex-grow-1">
                      <h6 class="text-overline text-subtitle-2">{{ $lang('reservations.fields.unit') }}</h6>
                      <p class="mb-0" v-if="form.unit">{{form.unit.name}}</p>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="12" class="mt-0 pt-0" align="center" v-if="!!form.room_type_id">
                  <v-chip :color="form.room_type.unit_category.color" class="white--text text-overline" label small>
                    <v-icon color="white" small class="mr-2">mdi-shape</v-icon>
                    {{ form.room_type.unit_category.name }}
                  </v-chip>
                  <v-row class="mt-1">
                    <v-col>
                      <c-refundable-menu
                        v-model="form.is_refundable"
                        :label="$lang('main.words.refundability')"
                        x-small
                        block
                        read-only
                      />
                    </v-col>
                    <v-col>
                      <c-meal-plan-menu
                        v-model="form.meal_plan"
                        :label="$lang('main.words.meal_plan')"
                        x-small
                        block
                        read-only
                      />
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-container>
            <v-divider></v-divider>
            <v-container v-if="$role('CLEANING_SERVICE') && $can('VIEW_RESERVATION_SERVICE_NOTES')">
              <v-row>
                <v-col>
                  <span class="subtitle-2">{{ $lang('reservations.fields.service_note') }}: </span>
                  <span class="caption">{{ form.service_note }}</span>
                </v-col>
              </v-row>
            </v-container>
            <v-container class="px-0" v-if="$can('VIEW_RESERVATION_SERVICE_NOTES')">
              <v-row>
                <v-col>
                  <span class="subtitle-2">{{ $lang('reservations.fields.internal_note') }}: </span>
                  <span class="caption">{{ form.internal_note }}</span>
                </v-col>
              </v-row>
            </v-container>
            <v-container class="px-0">
              <v-row v-if="$can('VIEW_RESERVATION_AMOUNTS')">
                <v-col cols="12" xs="12" md="12">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <tbody>
                        <tr>
                          <td style="width: 50%">{{ $lang('reservations.fields.amount') }}</td>
                          <td class="text-right">{{ $money(form.amount, form.currency) }}</td>
                        </tr>
                        <tr>
                          <td>{{ $lang('reservations.fields.cleaning_fee') }}</td>
                          <td class="text-right">{{ $money(form.cleaning_fee, form.currency) }} +</td>
                        </tr>
                        <tr>
                          <td>{{ $lang('reservations.fields.extras') }}</td>
                          <td class="text-right">{{ $money(totalExtra, form.currency) }} +</td>
                        </tr>
                        <tr>
                          <td>{{ $lang('reservations.labels.payments.title') }}</td>
                          <td class="text-right">{{ $money(totalPayment, form.currency) }} -</td>
                        </tr>
                        <tr>
                          <td>{{ $lang('reservations.fields.city_tax') }}</td>
                          <td class="text-right">{{ $money(form.city_tax, form.currency) }} +</td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-col>
              </v-row>
              <v-row v-if="$can('VIEW_RESERVATION_AMOUNTS')">
                <v-col cols="12" xs="12" md="12" class="mb-5">
                  <v-simple-table dense>
                    <template v-slot:default>
                      <tbody>
                        <tr>
                          <td style="width: 50%">{{ $lang('reservations.labels.total') }}</td>
                          <td class="text-right">{{ $money(totalGross, form.currency) }}</td>
                        </tr>
                        <tr>
                          <th class="text-subtitle-1">{{ $lang('reservations.labels.payments.to_pay') }}</th>
                          <th class="text-subtitle-1 text-right" :class="toPay > 0 ? 'red--text' : 'green--text'">{{ $money(toPay, form.currency) }}</th>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </v-col>
              </v-row>
            </v-container>
          </v-col>
        </v-col>
      </v-row>
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <v-btn
            v-if="$can('EDIT_RESERVATIONS')"
            @click="openEditing(itemId);"
            v-on="on"
            block
            color="primary"
          >
            <v-icon small class="mr-1">mdi-pencil</v-icon>
            {{ $lang('main.crud.update') }}
          </v-btn>
        </template>
        <span>{{ $lang('main.crud.edit') }}</span>
      </v-tooltip>
    </v-container>

    <v-dialog v-model="isOpenCardDetails" width="700">
      <card-details
        v-if="isOpenCardDetails"
        :data="{
          ccs_pan: form.ccs_pan,
          ccs_type: form.ccs_type,
          ccs_bank: form.ccs_bank,
          ccs_token: form.ccs_token,
          ccs_brand: form.ccs_brand,
          ccs_scheme: form.ccs_scheme,
          ccs_prepaid: form.ccs_prepaid,
          ccs_card_holder: form.ccs_card_holder,
          ccs_expire_date: form.ccs_expire_date,
          api: {
            mobile: form.property.mobile,
            email: form.property.email,
            property_id: form.property.id,
            user: form.property.secom_api_user,
          }
        }"
        @cancel="closeCardDetails"
      >
      </card-details>
    </v-dialog>
    <v-confirmation
      v-model="isOpenForceDelete"
      :options="{
        loading: replaceRequest.loading,
        yes: () => forceDeleteReservation()
      }"
    />
  </v-navigation-drawer>
</template>

<script>
import { mapState } from "vuex";

import form from "@/pages/reservations/mixins/form";
import cards from "@/pages/reservations/mixins/cards";
import totals from "@/pages/reservations/mixins/totals";

// import EditingForm from "@/pages/reservations/EditingForm";
import CardDetails from "@/pages/reservations/partials/CardDetails";

import {
  GET_RESERVATIONS_SINGLE,
  REPLACE_RESERVATIONS,
  REMOVE_RESERVATIONS,
  FORCE_REMOVE_RESERVATIONS,
} from "@/store/reservations/actions";

export default {
  name: "ViewDrawer",
  mixins: [
    form,
    cards,
    totals
  ],
  components: {
    // EditingForm,
    CardDetails
  },
  props: [
    "itemId"
  ],
  data() {
    return {
      drawer: true,
      isOpenEditing: false,
      checkInStatus: false,
    };
  },
  beforeMount() {
    this.$store.dispatch(GET_RESERVATIONS_SINGLE, {
      id: this.itemId,
      params: {
        editing: {
          with_linked: false
        }
      }
    });
  },
  methods: {
    populate(item) {
      this.form = { ...item };
    },
    toggled(state) {
      if (!state) {
        this.$emit("close");
      }
    },
    openEditing(id) {
      this.$emit('close');
      this.$emit('openEditing', id);
    },
    deleteReservation() {
      this.$store.dispatch(REMOVE_RESERVATIONS, {
        id: this.itemId
      }).then((response) => {
        this.$emit('removed', response);
      });
    },
    replaceReservation(params) {
      this.$store.dispatch(REPLACE_RESERVATIONS, {
        id: this.itemId,
        params
      }).then((response) => {
        this.$emit('updated', response);
      });
    },
    forceDeleteReservation() {
        this.$store.dispatch(FORCE_REMOVE_RESERVATIONS, {
           id: this.itemId,
        }).then((response) => {
          this.$emit('removed', response);
        });
      },
  },
  computed: {
    ...mapState({
      item: state => state.reservations.getSingleRequest.item,
      loading: state => state.reservations.getSingleRequest.loading,
      deletionRequest: state => state.reservations.deletionRequest,
      replaceRequest: state => state.reservations.replaceRequest,
    }),
    initialized() {
      return !this.loading;
    },
    loadingAggregated() {
      return this.loading || this.deletionRequest.loading || this.replaceRequest.loading;
    },
    createdDateTime() {
      if (!this.form.created_at) {
        return null;
      }

      return this.$toDateTimeFormat(this.form.created_at)
    },
    updatedDateTime() {
      if (!this.form.updated_at) {
        return null;
      }

      return this.$toDateTimeFormat(this.form.updated_at)
    },
  },
};
</script>

<style scoped>
.wrap-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: clip;
  word-break: keep-all;
}
</style>
